/**
 * ProfileQuickEditForm — iter1093
 *
 * Inline mini-form показван в ProfileCompletenessBanner. Съсредоточен само
 * върху НЕПОПЪЛНЕНИТЕ 3 полета — user не трябва да отваря целия профил.
 * PATCH-ва през `/api/auth/me` към точните nested paths.
 */
import React, { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { Loader2, Check } from "lucide-react";
import { readProfileFields } from "@/lib/profileCompleteness";

// Всички разпознати fast-fields, подредени по бизнес важност (най-нужните първи).
const FAST_FIELDS = [
  {
    key: "age", section: "basics",
    label_bg: "Възраст", label_en: "Age",
    type: "number", min: 13, max: 100, unit: "г.",
    path: "profile.personal.age",
    parse: (v) => parseInt(v, 10),
  },
  {
    key: "gender", section: "basics",
    label_bg: "Пол", label_en: "Sex",
    type: "select", options: [
      { value: "male", label_bg: "Мъж", label_en: "Male" },
      { value: "female", label_bg: "Жена", label_en: "Female" },
    ],
    path: "profile.personal.gender",
    parse: (v) => String(v),
  },
  {
    key: "height_cm", section: "basics",
    label_bg: "Височина", label_en: "Height",
    type: "number", min: 100, max: 230, unit: "см",
    path: "profile.body.height_cm",
    parse: (v) => parseFloat(v),
  },
  {
    key: "weight_kg", section: "basics",
    label_bg: "Тегло", label_en: "Weight",
    type: "number", min: 30, max: 250, unit: "кг",
    path: "profile.body.weight_kg",
    parse: (v) => parseFloat(v),
  },
  {
    key: "activity_level", section: "lifestyle",
    label_bg: "Активност", label_en: "Activity",
    type: "select", options: [
      { value: "sedentary", label_bg: "Заседнал", label_en: "Sedentary" },
      { value: "light", label_bg: "Лека", label_en: "Light" },
      { value: "moderate", label_bg: "Средна", label_en: "Moderate" },
      { value: "high", label_bg: "Висока", label_en: "High" },
      { value: "very_high", label_bg: "Много висока", label_en: "Very high" },
    ],
    path: "profile.lifestyle.activityLevel",
    parse: (v) => String(v),
  },
  {
    key: "sleep_hours", section: "lifestyle",
    label_bg: "Сън", label_en: "Sleep",
    type: "number", min: 3, max: 14, unit: "ч.",
    path: "profile.lifestyle.sleepHours",
    parse: (v) => String(v),
  },
  {
    key: "primary_goal", section: "lifestyle",
    label_bg: "Основна цел", label_en: "Goal",
    type: "select", options: [
      { value: "weight_loss", label_bg: "Отслабване", label_en: "Weight loss" },
      { value: "muscle_gain", label_bg: "Мускулна маса", label_en: "Muscle gain" },
      { value: "energy", label_bg: "Енергия", label_en: "Energy" },
      { value: "longevity", label_bg: "Дълголетие", label_en: "Longevity" },
      { value: "general_health", label_bg: "Общо здраве", label_en: "General" },
    ],
    path: "profile.lifestyle.primaryGoal",
    parse: (v) => String(v),
  },
];

/**
 * Помощник — превръща dot-path в nested $set payload. Използваме за да
 * не гърми ALLOWED_TOP_KEYS filter-a в /auth/me PATCH-a.
 */
function setDotPath(obj, path, value) {
  const parts = path.split(".");
  let cur = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    if (!cur[parts[i]] || typeof cur[parts[i]] !== "object") cur[parts[i]] = {};
    cur = cur[parts[i]];
  }
  cur[parts[parts.length - 1]] = value;
}

/**
 * @param {Object} profile — data от `/auth/me`.profile (nested shape)
 * @param {() => void} onSaved — callback след успешен save (refreshUser + reload).
 */
export default function ProfileQuickEditForm({ profile, onSaved }) {
  const { API, refreshUser } = useAuth();
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const bg = language === "bg";

  const filled = readProfileFields(profile || {});

  // Взимаме първите 3 непопълнени полета.
  const missing = useMemo(() => {
    return FAST_FIELDS.filter((f) => {
      const v = filled[f.key];
      return v === null || v === undefined || v === "" || Number(v) === 0;
    }).slice(0, 3);
  }, [profile]);

  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  if (missing.length === 0) return null;

  const allFilled = missing.every((f) => {
    const v = values[f.key];
    return v !== undefined && v !== "";
  });

  const save = async () => {
    setSaving(true);
    // Ако user попълва gender=male и е избрано женско-only поле → drop
    const payload = {};
    for (const f of missing) {
      const raw = values[f.key];
      if (raw === undefined || raw === "") continue;
      const parsed = f.parse(raw);
      setDotPath(payload, f.path, parsed);
    }
    try {
      await axios.patch(`${API}/auth/me`, payload);
      toast.success(bg
        ? `Записахме ${missing.length} полета — благодарим!`
        : `Saved ${missing.length} fields — thanks!`);
      if (refreshUser) await refreshUser();
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || (bg ? "Грешка при запис" : "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const inputCls = `w-full text-sm px-2 py-1.5 rounded-lg border ${
    isDark
      ? "bg-white/5 border-white/15 text-slate-100 placeholder-slate-500 focus:border-purple-400"
      : "bg-white border-slate-300 text-slate-900 placeholder-slate-400 focus:border-purple-500"
  } focus:outline-none focus:ring-2 focus:ring-purple-400/30`;

  return (
    <div
      data-testid="banner-quick-edit-form"
      className={`mt-3 rounded-xl border p-3 ${
        isDark ? "bg-white/[0.04] border-white/10" : "bg-white/70 border-purple-200"
      }`}
    >
      <div className={`text-[10px] uppercase font-bold tracking-wider mb-2 ${
        isDark ? "text-purple-300" : "text-purple-700"
      }`}>
        {bg
          ? `Попълни ${missing.length} полета за 20 секунди`
          : `Fill ${missing.length} fields in 20 seconds`}
      </div>
      <div className={`grid gap-2 ${missing.length === 3 ? "grid-cols-3" : missing.length === 2 ? "grid-cols-2" : "grid-cols-1"}`}>
        {missing.map((f) => (
          <label key={f.key} className="flex flex-col gap-1" data-testid={`banner-quick-edit-${f.key}`}>
            <span className={`text-[10px] font-semibold ${isDark ? "text-slate-300" : "text-slate-700"}`}>
              {bg ? f.label_bg : f.label_en}
              {f.unit ? <span className="opacity-60 ml-1">({f.unit})</span> : null}
            </span>
            {f.type === "select" ? (
              <select
                value={values[f.key] || ""}
                onChange={(e) => setValues((prev) => ({ ...prev, [f.key]: e.target.value }))}
                className={inputCls}
                data-testid={`banner-quick-edit-${f.key}-input`}
              >
                <option value="">—</option>
                {f.options.map((o) => (
                  <option key={o.value} value={o.value}>{bg ? o.label_bg : o.label_en}</option>
                ))}
              </select>
            ) : (
              <input
                type="number"
                min={f.min}
                max={f.max}
                inputMode="decimal"
                value={values[f.key] ?? ""}
                onChange={(e) => setValues((prev) => ({ ...prev, [f.key]: e.target.value }))}
                className={inputCls}
                data-testid={`banner-quick-edit-${f.key}-input`}
              />
            )}
          </label>
        ))}
      </div>
      <button
        type="button"
        disabled={!allFilled || saving}
        onClick={save}
        data-testid="banner-quick-edit-save"
        className={`mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold shadow-sm ${
          allFilled && !saving
            ? (isDark ? "bg-emerald-500 hover:bg-emerald-400 text-white" : "bg-emerald-600 hover:bg-emerald-700 text-white")
            : (isDark ? "bg-white/10 text-slate-400 cursor-not-allowed" : "bg-slate-200 text-slate-500 cursor-not-allowed")
        }`}
      >
        {saving
          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
          : <Check className="w-3.5 h-3.5" />}
        {saving ? (bg ? "Записва..." : "Saving...") : (bg ? "Запиши" : "Save")}
      </button>
    </div>
  );
}
